import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard } from "lucide-react";

const HELCIM_SCRIPT = "https://secure.helcim.app/helcim-pay/services/start.js";

export default function HelcimPayButton({ matchId, amount, onPaid }) {
  const [loading, setLoading] = useState(false);
  const tokenRef = useRef(null);

  useEffect(() => {
    if (document.querySelector(`script[src="${HELCIM_SCRIPT}"]`)) return;
    const s = document.createElement("script");
    s.src = HELCIM_SCRIPT;
    s.async = true;
    document.body.appendChild(s);
  }, []);

  useEffect(() => {
    const onMessage = async (event) => {
      const token = tokenRef.current;
      if (!token || !event.data) return;
      if (event.data.eventName !== `helcim-pay-js-${token}`) return;

      if (event.data.eventStatus === "SUCCESS") {
        let payload = event.data.eventMessage;
        if (typeof payload === "string") {
          try { payload = JSON.parse(payload); } catch (e) { console.warn("Helcim payload not JSON, keeping raw string", e); }
        }
        try {
          await api.post(`/matches/${matchId}/pay/confirm`, { rawDataResponse: payload });
          if (window.removeHelcimPayIframe) window.removeHelcimPayIframe();
          toast.success("Payment successful — fee secured!");
          tokenRef.current = null;
          onPaid && onPaid();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Payment verification failed");
        }
      } else if (event.data.eventStatus === "ABORTED") {
        toast.error("Payment failed or was cancelled");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [matchId, onPaid]);

  const start = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/matches/${matchId}/pay/initialize`);
      tokenRef.current = data.checkoutToken;
      if (window.appendHelcimPayIframe) {
        window.appendHelcimPayIframe(data.checkoutToken, true);
      } else {
        toast.error("Payment module still loading — please try again in a moment.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      data-testid="pay-fee-btn"
      disabled={loading}
      onClick={start}
      className="mt-3 w-full rounded-full hard-shadow-sm hover:-translate-y-0.5 transition-transform duration-200"
    >
      {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
      Authorize ${Number(amount || 0).toFixed(2)} hold
    </Button>
  );
}
